"use client"

import type { ShareableAgent, WorkflowNode } from "@/lib/community/publish-helpers"

interface PublishTeamViewProps {
  agents: ShareableAgent[]
  workflowNodes: WorkflowNode[]
  scenarioTitle: string
}

const ROLE_BG: Record<string, string> = {
  环境: "bg-blue-500/10 text-blue-500",
  数据: "bg-emerald-500/10 text-emerald-500",
  研究: "bg-indigo-500/10 text-indigo-500",
  调研: "bg-indigo-500/10 text-indigo-500",
  采集: "bg-indigo-500/10 text-indigo-500",
  转录: "bg-violet-500/10 text-violet-500",
  整理: "bg-teal-500/10 text-teal-500",
  组织: "bg-teal-500/10 text-teal-500",
  协作: "bg-amber-500/10 text-amber-500",
  设计: "bg-pink-500/10 text-pink-500",
  创作: "bg-pink-500/10 text-pink-500",
  整合: "bg-emerald-500/10 text-emerald-500",
  交付: "bg-red-500/10 text-red-500",
  运营: "bg-orange-500/10 text-orange-500",
  增长: "bg-orange-500/10 text-orange-500",
  客服: "bg-cyan-500/10 text-cyan-500",
  服务: "bg-cyan-500/10 text-cyan-500",
  法律: "bg-slate-500/10 text-slate-500",
  法务: "bg-slate-500/10 text-slate-500",
  合规: "bg-slate-500/10 text-slate-500",
  风控: "bg-red-500/10 text-red-500",
  安全: "bg-blue-500/10 text-blue-500",
  解析: "bg-violet-500/10 text-violet-500",
  文档: "bg-violet-500/10 text-violet-500",
  量化: "bg-amber-500/10 text-amber-500",
  策略: "bg-emerald-500/10 text-emerald-500",
  交易: "bg-red-500/10 text-red-500",
  开发: "bg-blue-500/10 text-blue-500",
  分析: "bg-indigo-500/10 text-indigo-500",
  记录: "bg-slate-500/10 text-slate-500",
}

const DEFAULT_ROLE_BG = "bg-secondary/50 text-muted-foreground"

export function PublishTeamView({ agents, workflowNodes, scenarioTitle }: PublishTeamViewProps) {
  const roles = agents.map((a) => a.role)
  const uniqueRoles = [...new Set(roles)]
  const totalTools = agents.reduce((sum, a) => sum + a.toolCount, 0)
  const totalSkills = agents.reduce((sum, a) => sum + a.skillNames.length, 0)

  if (agents.length === 0) {
    return (
      <p className="text-caption text-muted-foreground/50 text-center py-4">
        该场景暂无智能体团队数据
      </p>
    )
  }

  return (
    <div className="space-y-2.5">
      <p className="text-caption text-foreground/70 leading-relaxed">
        完整的 {agents.length} 智能体协作团队，覆盖{uniqueRoles.join("、")}等角色
      </p>

      {/* Team container */}
      <div className="rounded-xl overflow-hidden border border-red-500/15">
        {/* Header */}
        <div
          className="px-3 py-2 flex items-center gap-2"
          style={{
            background: "linear-gradient(135deg, rgba(239,68,68,0.08), rgba(239,68,68,0.03))",
          }}
        >
          {/* Avatar stack */}
          <div className="flex -space-x-1.5">
            {agents.slice(0, 6).map((agent, i) => (
              <div
                key={agent.id}
                className="h-5 w-5 rounded-full border-[1.5px] border-background flex items-center justify-center text-[8px] font-bold"
                style={{
                  background: `hsl(${(i * 360) / agents.length}, 40%, 70%)`,
                  color: "white",
                  zIndex: agents.length - i,
                }}
              >
                {agent.name.charAt(0)}
              </div>
            ))}
            {agents.length > 6 && (
              <div className="h-5 w-5 rounded-full border-[1.5px] border-background bg-secondary/70 flex items-center justify-center text-[8px] text-muted-foreground font-medium">
                +{agents.length - 6}
              </div>
            )}
          </div>
          <span className="text-caption font-semibold text-foreground/80">
            智能体团队 · {agents.length} 位成员
          </span>
        </div>

        {/* Members */}
        <div className="px-2 py-2 space-y-1">
          {agents.map((agent) => {
            const deps = workflowNodes.find((n) => n.id === agent.id)?.dependencies ?? []
            const depNames = deps
              .map((depId) => agents.find((a) => a.id === depId)?.name)
              .filter(Boolean)

            return (
              <div
                key={agent.id}
                className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 bg-secondary/15"
              >
                {/* Name + role */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-caption font-semibold text-foreground truncate">
                      {agent.name}
                    </span>
                    <span
                      className={`text-nano px-1.5 py-0.5 rounded-full ${ROLE_BG[agent.role] ?? DEFAULT_ROLE_BG}`}
                    >
                      {agent.role}
                    </span>
                  </div>
                  {depNames.length > 0 && (
                    <p className="text-nano text-muted-foreground/60 mt-0.5">
                      依赖：{depNames.join("、")}
                    </p>
                  )}
                </div>

                {/* Stats */}
                <div className="flex items-center gap-1.5 text-nano text-muted-foreground shrink-0">
                  <span>{agent.skillNames.length} 技能</span>
                  <span className="text-border">·</span>
                  <span>{agent.toolCount} 工具</span>
                </div>
              </div>
            )
          })}
        </div>

        {/* Footer stats */}
        <div className="px-3 py-1.5 border-t border-red-500/10 flex items-center gap-3 text-micro text-muted-foreground">
          <span>共 {totalSkills} 个技能</span>
          <span>·</span>
          <span>{totalTools} 次工具调用</span>
          <span>·</span>
          <span>{uniqueRoles.length} 个角色</span>
        </div>
      </div>
    </div>
  )
}
